/** Only stable, internal trip surfaces belong in the return-to-trip shortcut. */
export function tripResumeHref(tripId: string, pathname: string, search = ''): string | null {
  if (!/^[a-zA-Z0-9_-]+$/.test(tripId) || tripId === 'active') return null;
  const base = `/trips/${tripId}`;
  const path = pathname.replace(/\/$/, '');
  const query = new URLSearchParams(search);
  if (path === base) {
    const view = query.get('view');
    return view && ['overview', 'itinerary', 'map'].includes(view) ? `${base}?view=${view}` : base;
  }
  if (path === `${base}/chat` || path === `${base}/sleep`) return path;
  if (path === `${base}/details` || path === `${base}/dashboard`) {
    const tool = query.get('tool');
    return `${base}/details${tool && ['details', 'sharing', 'packing', 'budget', 'documents', 'sleep', 'export'].includes(tool) ? `?tool=${tool}` : ''}`;
  }
  return null;
}

type TripResume = { tripId: string; href: string };
const storageKey = (userId: string) => `roam:tripResume:${userId}`;

export function rememberTripResume(
  userId: string,
  tripId: string,
  pathname: string,
  search: string,
) {
  const href = tripResumeHref(tripId, pathname, search);
  if (!userId || !href) return;
  try {
    window.sessionStorage.setItem(storageKey(userId), JSON.stringify({ tripId, href }));
  } catch {
    // Storage is optional; the server's last accessible trip remains a fallback.
  }
}

/** Revalidate both the route and access; never trust a remembered trip ID alone. */
export function readTripResume(
  userId: string | undefined,
  accessibleTripIds: string[],
): TripResume | null {
  if (!userId) return null;
  try {
    const value: unknown = JSON.parse(window.sessionStorage.getItem(storageKey(userId)) ?? 'null');
    if (!value || typeof value !== 'object' || !('tripId' in value) || !('href' in value))
      return null;
    if (
      typeof value.tripId !== 'string' ||
      typeof value.href !== 'string' ||
      !accessibleTripIds.includes(value.tripId)
    )
      return null;
    const [pathname, search = ''] = value.href.split('?');
    const href = tripResumeHref(value.tripId, pathname!, search);
    return href === value.href ? { tripId: value.tripId, href } : null;
  } catch {
    return null;
  }
}
